import { useFormik } from 'formik';
import React, { useCallback } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { DistributiveOmit } from "../../../../types/utils";
import * as Api from '../../../api';
import { TApiOrderDetails, TOrderCreateParams } from "../../../api/endpoints/Order";
import OrderPolicy from "../../../policies/OrderPolicy";
import { createOrder } from "../../../store/action-creators/orders";
import { TRootState } from '../../../store/reducers/RootReducer';
import { selectUser } from "../../../store/selectors/UserSelectors";
import HelpTexts from "../../HelpTexts";
import Checkbox from "../../Inputs/Checkbox/Checkbox";
import Footnote from "../../RealtimeProductConfigurator/Footnote";
import FormError from "../../RealtimeProductConfigurator/FormError";
import StartDateField from "./StartDateField";

export interface IOrderFormProps {
  onSubmit: (order: TApiOrderDetails) => void;
  orderProps: DistributiveOmit<TOrderCreateParams, 'name' | 'research' | 'start_date'>
}

interface IOrderCreationFormValues {
  name: string;
  start_date: string;
  research: boolean;
}

const OrderForm = (props: IOrderFormProps) => {
  const { orderProps, onSubmit } = props;
  const dispatch = useDispatch<ThunkDispatch<TRootState, any, Action>>();
  const user = useSelector(selectUser);

  const handleSubmit = useCallback((values: IOrderCreationFormValues) => {
    const { name, research, start_date } = values;

    return dispatch(createOrder({
      ...orderProps,
      name,
      start_date,
      ...(OrderPolicy.canRequestResearchLicence(user, orderProps) && { research })
    })).then((order) => onSubmit(order));
  }, [orderProps, onSubmit, dispatch]);

  const formik = useFormik<IOrderCreationFormValues>({
    initialValues: {
      name: '',
      start_date: '',
      research: false
    },
    onSubmit: (values, { setSubmitting, setErrors }) => {
      handleSubmit(values).then(() => {
        setSubmitting(false);
      }).catch(error => {
        setSubmitting(false);

        if (error instanceof Api.ValidationError) {
          const errors = error.data;
          setErrors(errors);
        }
      });
    }
  });

  const onResearchChange = useCallback((checked: boolean) => {
    formik.setFieldValue('research', checked);
  }, [formik]);

  return (
    <Form onSubmit={formik.handleSubmit} className="order-create-form">
      <Form.Group controlId="orderName">
        <Form.Label>Order Name</Form.Label>
        <Form.Control
          name="name"
          type="text"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.name}
        />
        <FormError message={formik.errors.name} when={formik.touched.name && Boolean(formik.errors.name)} />
      </Form.Group>

      <StartDateField formik={formik} orderCategory={orderProps.category} />

      {
        OrderPolicy.canRequestResearchLicence(user, orderProps) &&
        <Form.Group controlId="research" className="margin-bottom-xs">
          <Checkbox
            name="research"
            value=""
            checked={formik.values.research}
            onChange={onResearchChange}
            label="Research Project Fee-Waiver Service Agreement"
          />

          <Footnote text={HelpTexts.researchLicenceInfo()} />
        </Form.Group>
      }

      <Button type="submit" disabled={formik.isSubmitting}>
        {formik.isSubmitting ? 'Creating...' : 'Create'}
      </Button>
    </Form>
  );
};

export default OrderForm;
